import React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { TableHead, useMediaQuery, useTheme } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ProgramTemplateDto,
  ProgramTemplateCategory,
  ProgramTemplateOrderingMode,
} from "@growth-machine-llc/stridist-api-client";
import ProgramTemplatesService from "../../services/ProgramTemplatesService";
import { AllOption, ProgramStatus } from "../../constants";
import { useProgramListFilterContext } from "../../contexts/ProgramLibraryFilterContext";
import { useDebounce } from "../../hooks/useDebounce";
import clsx from "clsx";
import { ProgramsTableRow } from "./ProgramsTableRow";
import { CategoryFilter } from "./filters/CategoryFilter";
import { ProgramsTableCardsView } from "./ProgramsTableCardsView";
import {
  ProgramLibraryCardSkeleton,
  ProgramLibraryTableSkeleton,
} from "../loading/ProgramLibrarySkeletons";
import { COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE } from "../../routes/routes";
import { useNavigate } from "react-router-dom";
import { JourneyAnchors } from "../../constants";
import { CoachLibraryTableScreen } from "../library/CoachLibraryTableScreen";
import { NameSortButton } from "../exercise-library/filters/NameSortButton";

export interface ProgramsTableProps {
  status: ProgramStatus;
}

export const getProgramTemplateLibraryWidths = () => {
  return {
    thumbnail: "6%",
    title: "53%",
    category: "18%",
    privacy: "18%",
    actions: "5%",
  };
};

const COLUMNS_COUNT = 5;

export const COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY = "program-library-assets";

export function ProgramsTable(props: ProgramsTableProps) {
  const { status } = props;
  const columnWidths = getProgramTemplateLibraryWidths();

  const { breakpoints } = useTheme();
  const mdUp = useMediaQuery(breakpoints.up("md"));

  const {
    query,
    category,
    privacy,
    page,
    rowsPerPage,
    sortKey,
    handleCategoryChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortKeyChange,
  } = useProgramListFilterContext();

  const navigate = useNavigate();

  const debounceQuery = useDebounce(query, 500);

  const isAllCategoriesSelected = category === AllOption;

  const allCategories = React.useMemo(() => {
    return Object.values(ProgramTemplateCategory);
  }, []);

  const categories = React.useMemo(() => {
    if (isAllCategoriesSelected) {
      return allCategories;
    }
    if (Array.isArray(category)) {
      return category;
    }
    return category ? [category] : allCategories;
  }, [isAllCategoriesSelected, category, allCategories]);

  const templatePrivacyTypeQueryParam = privacy === AllOption ? null : privacy;

  const categoryQueryParam =
    !isAllCategoriesSelected &&
    !Array.isArray(category) &&
    category !== AllOption
      ? (category as ProgramTemplateCategory)
      : null;

  const orderingMode = ProgramTemplateOrderingMode.Library;

  const { data: programTemplatesData, isFetching } = useQuery({
    queryKey: [
      COACH_PROGRAM_LIBRARY_ASSETS_QUERY_KEY,
      {
        page,
        rowsPerPage,
        query: debounceQuery,
        category: categoryQueryParam,
        templateType: templatePrivacyTypeQueryParam,
        sortKey,
        orderingMode,
        status,
      },
    ],
    queryFn: () =>
      ProgramTemplatesService.getProgramTemplates(
        [status],
        categoryQueryParam,
        templatePrivacyTypeQueryParam,
        sortKey,
        orderingMode,
      ),
    placeholderData: keepPreviousData,
  });

  const allTemplates = programTemplatesData?.programTemplates || [];

  const filteredTemplates = React.useMemo(() => {
    let filtered = allTemplates;

    if (debounceQuery) {
      const lowerQuery = debounceQuery.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.name?.toLowerCase().includes(lowerQuery) ||
          template.description?.toLowerCase().includes(lowerQuery),
      );
    }

    if (!isAllCategoriesSelected && categories.length > 0) {
      filtered = filtered.filter((template) =>
        categories.includes(template.category),
      );
    }

    return filtered;
  }, [allTemplates, debounceQuery, category, isAllCategoriesSelected]);

  const paginatedTemplates = React.useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredTemplates.slice(startIndex, endIndex);
  }, [filteredTemplates, page, rowsPerPage]);

  const handleEditProgram = React.useCallback(
    (template: ProgramTemplateDto) => {
      if (template.slug && template.canEdit) {
        const link = COACH_PROGRAM_LIBRARY_TEMPLATE_CURRICULUM_ROUTE.replace(
          ":slug",
          template.slug,
        );
        navigate(link, { state: { title: template?.name } });
      }
    },
    [navigate],
  );

  const renderTableHead = React.useCallback(
    () =>
      mdUp && (
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: columnWidths.thumbnail }}>
              Thumbnail
            </TableCell>
            <TableCell sx={{ width: columnWidths.title }}>
              <NameSortButton
                sortKey={sortKey}
                onChangeSortKey={(nextSort) => {
                  handleSortKeyChange(nextSort);
                  handlePageChange(1);
                }}
              />
            </TableCell>
            <TableCell align="center" sx={{ width: columnWidths.category }}>
              <CategoryFilter
                onChange={handleCategoryChange}
                contextFilters={categories}
              />
            </TableCell>
            <TableCell align="center" sx={{ width: columnWidths.privacy }}>
              Access
            </TableCell>
            <TableCell align="center" sx={{ width: columnWidths.actions }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
      ),
    [
      mdUp,
      columnWidths,
      sortKey,
      handleSortKeyChange,
      handlePageChange,
      handleCategoryChange,
      categories,
    ],
  );

  const renderRow = React.useCallback(
    (template: ProgramTemplateDto, index: number) =>
      mdUp && (
        <ProgramsTableRow
          template={template}
          onSelectedTemplate={handleEditProgram}
          className={
            index === 0
              ? JourneyAnchors.PROGRAM_LIBRARY_FIRST_LIST_ITEM
              : undefined
          }
        />
      ),
    [mdUp, handleEditProgram],
  );

  const renderMobileCard = React.useCallback(
    (template: ProgramTemplateDto, index: number) =>
      !mdUp && (
        <ProgramsTableCardsView
          template={template}
          onSelectedTemplate={handleEditProgram}
          className={
            index === 0
              ? JourneyAnchors.PROGRAM_LIBRARY_FIRST_LIST_ITEM
              : undefined
          }
        />
      ),
    [mdUp, handleEditProgram],
  );

  const renderMobileFilters = React.useCallback(
    () =>
      !mdUp && (
        <CategoryFilter
          onChange={handleCategoryChange}
          contextFilters={categories}
        />
      ),
    [mdUp, handleCategoryChange, categories],
  );

  const renderSkeletonTable = React.useCallback(
    (className?: string) =>
      mdUp &&
      Array.from(new Array(rowsPerPage)).map((_, index) => (
        <React.Fragment key={index}>
          <ProgramLibraryTableSkeleton className={clsx(className)} />
        </React.Fragment>
      )),
    [mdUp, rowsPerPage],
  );

  const renderSkeletonCards = React.useCallback(
    () =>
      !mdUp &&
      Array.from(new Array(rowsPerPage)).map((_, index) => (
        <React.Fragment key={index}>
          <ProgramLibraryCardSkeleton />
        </React.Fragment>
      )),
    [mdUp, rowsPerPage],
  );

  return (
    <CoachLibraryTableScreen<ProgramTemplateDto>
      mode={mdUp ? "desktop" : "mobile"}
      data={paginatedTemplates}
      isLoading={isFetching}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      page={page - 1}
      onPageChange={(newPage) => handlePageChange(newPage + 1)}
      totalCount={filteredTemplates.length}
      renderTableHead={renderTableHead}
      renderRow={renderRow}
      renderMobileCard={renderMobileCard}
      renderMobileFilters={renderMobileFilters}
      renderSkeletonTable={renderSkeletonTable}
      renderSkeletonCards={renderSkeletonCards}
      emptyMessage="No programs found for current filters."
      colCount={COLUMNS_COUNT}
    />
  );
}
